
    import Vue from "vue";
    import Component from "vue-class-component";
    import { Route } from "vue-router";
    import { Watch } from "vue-property-decorator";
    import ConfirmDialogue from "@/vue/components/ConfirmDialogue.vue";
    import ApiButton from "@/vue/components/ApiButton.vue";
    import AdminWorkPermit from "@/vue/views/Admin/WorkPermit.vue";
    import ApiClient from "@/utilities/ApiClient";
    import Utils from "@/utilities/Utils";
    import { WorkPermitPerson } from "@/model/workPermits/WorkPermitPerson";
    import { IWorkPermitPublicInitResponse } from "@/model/workPermits/WorkPermitPublicInitResponse";
    import { IWorkPermitSaveResponse } from "@/model/IWorkPermitSaveResponse";

    enum StateType {
        None = 0,
        Error,
        Failed,
        ReadyToSign,
        JustSigned,
        ReadyToSurrender,
        JustSurrendered,
        Surrendered
    }

    @Component({
        components: { ApiButton, AdminWorkPermit, ConfirmDialogue }
    })
    export default class WorkPermit extends Vue {

        mounted(): void {
            this.onIdChanged(this.$router.currentRoute.params.workPermitId);
        }

        //
        // -- watchers
        //

        // if the id changes in the URL, the component *will not* reload by itself - so we need to handle it here
        @Watch("$route", { immediate: true, deep: true })
        onRouteChanged(route: Route): void {
            this.onIdChanged(route.params.workPermitId);
        }

        //
        // -- properties
        //

        workPermitId: string = Utils.emptyGuidValue;
        stateType = StateType; // to access in template
        currentState: StateType = StateType.None;
        alertMessage: string = "";
        siteName: string = "";
        person = new WorkPermitPerson();

        get title(): string {
            switch(this.currentState) {
                case StateType.Error: return "Work Permit Form - Error";
                case StateType.Failed: return "Work Permit Form - Failure";
                case StateType.ReadyToSign: return "Sign Work Permit Form";
                case StateType.JustSigned: return "Sign Work Permit Form";
                case StateType.ReadyToSurrender: return "Surrender Work Permit";
                case StateType.JustSurrendered: return "Surrender Work Permit";
                case StateType.Surrendered: return "Work Permit Form";
                default: return "Work Permit Form";
            }
        }

        get stateText(): string {
            return StateType[this.currentState];
        }

        get shouldShowAlert(): boolean {
            return this.currentState === StateType.Error
            || this.currentState === StateType.Failed
            || this.currentState === StateType.JustSigned
            || this.currentState === StateType.JustSurrendered
            || this.currentState === StateType.Surrendered;
        }

        get alertVariant(): string {
            switch(this.currentState) {
                case StateType.Error: return "error";
                case StateType.Failed: return "error";
                case StateType.JustSigned: return "success";
                case StateType.JustSurrendered: return "success";
                case StateType.Surrendered: return "info";
                default: return "error";
            }
        }

        get shouldShowForm(): boolean {
            return !Utils.isEmptyId(this.workPermitId) && this.currentState !== StateType.None;
        }

        //
        // -- methods
        //

        private async onIdChanged(workPermitId: string) {

            this.currentState = StateType.None;

            if (this.workPermitId === workPermitId) return;
            this.workPermitId = workPermitId;

            const response: IWorkPermitPublicInitResponse = await ApiClient.get(`/api/workPermit/publicAccessInit?id=${workPermitId}`);
            console.log("publicAccessInit response = ", response);
            if(response.isError) {
                this.currentState = StateType.Error;
            }
            else if(response.isSurrendered) {
                this.currentState = StateType.Surrendered;
            }
            else if(response.isSigned) {
                this.currentState = StateType.ReadyToSurrender;
            }
            else {
                this.currentState = StateType.ReadyToSign;
            }
            this.person.update(response.person);
            this.alertMessage = response.message;
            this.workPermitId = response.workPermitID;
        }

        showConfirmSignDialogue(): void {
            const dialog: ConfirmDialogue = this.$refs.signConfirmDialogue as ConfirmDialogue;
            dialog.show();
        }

        showConfirmSurrenderDialogue(): void {
            const dialog: ConfirmDialogue = this.$refs.surrenderConfirmDialogue as ConfirmDialogue;
            dialog.show();
        }

        async addSignature(): Promise<void> {
            const response: IWorkPermitSaveResponse = await ApiClient.get(`/api/workPermit/addSignature?workPermitID=${this.workPermitId}&email=${encodeURIComponent(this.person.email)}`, "work-permit-sign");
            if(response.isSuccess) {
                this.currentState = StateType.JustSigned;
                this.alertMessage = "The form has now been signed.  Thank you.";
            }
            else {
                this.currentState = StateType.Failed;
                this.alertMessage = response.message;
            }
        }

        async setSurrendered(): Promise<void> {
            const response: IWorkPermitSaveResponse = await ApiClient.get(`/api/workPermit/setSurrendered?workPermitID=${this.workPermitId}&email=${encodeURIComponent(this.person.email)}`, "work-permit-surrender");
            if(response.isSuccess) {
                this.currentState = StateType.JustSurrendered;
                this.alertMessage = "The form has now been surrendered.  Thank you.";
            }
            else {
                this.currentState = StateType.Failed;
                this.alertMessage = response.message;
            }
        }

    }
